import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProjectFormModal } from '@/features/operations-projects/components/ProjectFormModal';
import {
  useCompleteMilestone,
  useCreateProject,
  useProject,
  useUncompleteMilestone,
  useUpdateProject,
} from '@/features/operations-projects/hooks/useProjects';
import type { CreateProjectInput, UpdateProjectInput } from '@/features/operations-projects/types';
import { useProjectsKanban, kanbanKeys } from '../hooks/useOperationsMetrics';
import { toInitialSupportProjectQuote } from './ProjectsKanbanView';
import { QuoteOperationsDetailPanel } from './QuoteOperationsDetailPanel';
import type { ProjectKanbanCard, ProjectsKanbanData } from '../types';

export type QuoteOperationsTableRow = ProjectKanbanCard & {
  stageLabel: string;
  valuePercent: number;
};

export function flattenKanbanQuotes(data?: ProjectsKanbanData): QuoteOperationsTableRow[] {
  return (data?.columns ?? []).flatMap((column) =>
    column.projects.map((project) => ({
      ...project,
      stageLabel: column.label,
      valuePercent: column.valuePercent,
    }))
  );
}

function formatMoney(amount: number, currency?: string): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency || 'CAD',
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

export function ProjectsTableView() {
  const queryClient = useQueryClient();
  const [selectedQuote, setSelectedQuote] = useState<QuoteOperationsTableRow | null>(null);
  const [quoteForSupportProject, setQuoteForSupportProject] = useState<ProjectKanbanCard | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data, isLoading, isError } = useProjectsKanban({ filter: 'all' });
  const rows = useMemo(() => flattenKanbanQuotes(data), [data]);
  const selectedProjectId = selectedQuote?.supportProjectId || null;
  const { data: selectedProject, isLoading: projectLoading } = useProject(selectedProjectId ?? undefined);
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const completeMilestone = useCompleteMilestone();
  const uncompleteMilestone = useUncompleteMilestone();

  const initialSupportProjectQuote = useMemo(
    () => toInitialSupportProjectQuote(quoteForSupportProject),
    [quoteForSupportProject]
  );

  const handleRowClick = useCallback((row: QuoteOperationsTableRow) => {
    setSelectedQuote(row);
  }, []);

  const handleEditProject = useCallback(() => {
    if (!selectedProjectId) return;
    setIsEditModalOpen(true);
  }, [selectedProjectId]);

  const handleProjectUpdate = useCallback(
    async (input: UpdateProjectInput) => {
      if (!selectedProjectId) return;
      try {
        await updateProject.mutateAsync({ id: selectedProjectId, input });
        toast.success('Project updated successfully');
        setIsEditModalOpen(false);
      } catch {
        toast.error('Failed to update project');
      }
    },
    [selectedProjectId, updateProject]
  );

  const handleCreateSupportProject = useCallback(
    async (input: CreateProjectInput | UpdateProjectInput) => {
      try {
        const createdProject = await createProject.mutateAsync(input as CreateProjectInput);
        toast.success('Supporting project created');
        setQuoteForSupportProject(null);
        await queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
        setSelectedQuote((current) => {
          const currentQuoteId = current?.quoteId || current?.id;
          if (!current || currentQuoteId !== createdProject.quote_id) return current;
          return {
            ...current,
            supportProjectId: createdProject.id,
            type: createdProject.type,
            assignedToName: createdProject.assigned_to_name || current.assignedToName,
            accountManagerName: createdProject.account_manager_name || current.accountManagerName,
          };
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to create supporting project');
      }
    },
    [createProject, queryClient]
  );

  const handleProjectFormOpenChange = useCallback((open: boolean) => {
    if (isEditModalOpen) {
      setIsEditModalOpen(open);
    }
    if (!open) {
      setQuoteForSupportProject(null);
      setIsEditModalOpen(false);
    }
  }, [isEditModalOpen]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Failed to load quote operations table.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quote</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Owner</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  No quote operations records found.
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => handleRowClick(row)}
              >
                <TableCell>
                  <div className="font-medium">#{row.quoteNumber || row.projectNumber}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">
                    {row.quoteTitle || row.projectName || 'Untitled Quote'}
                  </div>
                </TableCell>
                <TableCell>{row.accountName || '-'}</TableCell>
                <TableCell>
                  <div className="font-medium">{row.stageLabel}</div>
                  <div className="text-xs text-muted-foreground">{row.milestoneProgress}</div>
                </TableCell>
                <TableCell>
                  <Badge variant={row.supportProjectId ? 'secondary' : 'outline'}>
                    {row.supportProjectId ? row.type : 'needs support'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{row.sensorsQty}</TableCell>
                <TableCell className="text-right">{formatMoney(row.amount, row.currency)}</TableCell>
                <TableCell>{row.assignedToName || row.accountManagerName || 'Unassigned'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Sheet
        open={!!selectedQuote}
        onOpenChange={(open) => { if (!open) setSelectedQuote(null); }}
      >
        <SheetContent className="w-[600px] sm:max-w-[600px] p-0 overflow-y-auto">
          <QuoteOperationsDetailPanel
            quoteCard={selectedQuote}
            supportProject={selectedProject}
            supportProjectLoading={projectLoading}
            onAddSupportProject={setQuoteForSupportProject}
            onEditSupportProject={handleEditProject}
            onCompleteStep={(stepKey) => {
              if (selectedProjectId) {
                completeMilestone.mutate({ projectId: selectedProjectId, milestoneKey: stepKey });
              }
            }}
            onUncompleteStep={(stepKey) => {
              if (selectedProjectId) {
                uncompleteMilestone.mutate({ projectId: selectedProjectId, milestoneKey: stepKey });
              }
            }}
            isCompletingStep={completeMilestone.isPending || uncompleteMilestone.isPending}
          />
        </SheetContent>
      </Sheet>

      <ProjectFormModal
        open={isEditModalOpen || !!quoteForSupportProject}
        onOpenChange={handleProjectFormOpenChange}
        project={isEditModalOpen ? selectedProject : undefined}
        initialQuote={!isEditModalOpen ? initialSupportProjectQuote : undefined}
        onSubmit={isEditModalOpen ? handleProjectUpdate : handleCreateSupportProject}
        isSubmitting={isEditModalOpen ? updateProject.isPending : createProject.isPending}
      />
    </div>
  );
}
